import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

interface DevelopmentStatusModalProps {
  open: boolean;
  kicker: string;
  title: string;
  body: string;
  closeLabel: string;
  titleId?: string;
  zIndexClassName?: string;
  onClose: () => void;
}

export default function DevelopmentStatusModal({
  open,
  kicker,
  title,
  body,
  closeLabel,
  titleId = 'development-status-modal-title',
  zIndexClassName = 'z-[90]',
  onClose,
}: DevelopmentStatusModalProps) {
  useEffect(() => {
    if (!open) return undefined;

    const root = document.documentElement;
    const body = document.body;
    const alreadyLocked = root.dataset.scrollLocked === 'true';

    if (alreadyLocked) return undefined;

    const scrollY = window.scrollY;
    const previousHtmlOverflow = root.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyPosition = body.style.position;
    const previousBodyTop = body.style.top;
    const previousBodyLeft = body.style.left;
    const previousBodyRight = body.style.right;
    const previousBodyWidth = body.style.width;

    root.dataset.scrollLocked = 'true';
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    return () => {
      delete root.dataset.scrollLocked;
      root.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      body.style.position = previousBodyPosition;
      body.style.top = previousBodyTop;
      body.style.left = previousBodyLeft;
      body.style.right = previousBodyRight;
      body.style.width = previousBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`fixed inset-0 ${zIndexClassName} flex items-center justify-center bg-black/78 px-6 backdrop-blur-md`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <motion.div
        initial={false}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0 }}
        className="relative w-full max-w-md rounded-[1.75rem] border border-white/10 bg-[#080808] p-7 text-white shadow-[0_28px_90px_-32px_rgba(168,255,53,0.45)]"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-white/70 transition hover:border-white/20 hover:text-white"
          aria-label={closeLabel}
        >
          <X size={17} aria-hidden />
        </button>
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.24em] text-univ-lime">
          {kicker}
        </p>
        <h2
          id={titleId}
          className="font-display text-3xl font-normal leading-tight text-white sm:text-4xl"
        >
          {title}
        </h2>
        <p className="mt-5 text-base leading-relaxed text-white/68">{body}</p>
        <button
          type="button"
          onClick={onClose}
          className="mt-7 inline-flex w-full items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-black transition hover:bg-univ-lime"
        >
          {closeLabel}
        </button>
      </motion.div>
    </div>,
    document.body,
  );
}
